import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ListChecks, Layers, Tags, Upload, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/brand";

const NAV = [
  { to: "/admin", end: true, label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/admin/questions", label: "Questions", icon: ListChecks, testid: "nav-questions" },
  { to: "/admin/categories", label: "Catégories", icon: Layers, testid: "nav-categories" },
  { to: "/admin/tags", label: "Tags", icon: Tags, testid: "nav-tags" },
  { to: "/admin/import", label: "Import", icon: Upload, testid: "nav-import" },
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const Sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-slate-200 p-5">
        <Logo size={40} />
        <div>
          <p className="font-display text-lg font-extrabold uppercase leading-none text-slate-900">Douzième Homme</p>
          <p className="text-xs text-slate-400">Back-office</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1 p-3">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                isActive ? "bg-dh-red text-white" : "text-slate-600 hover:bg-slate-100"
              }`
            }
          >
            <n.icon className="h-5 w-5" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-200 p-3">
        <p className="px-4 pb-2 text-xs text-slate-400 truncate">{user?.email}</p>
        <button
          data-testid="admin-logout-btn"
          onClick={doLogout}
          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-100"
        >
          <LogOut className="h-5 w-5" /> Déconnexion
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-slate-200 bg-white md:block">{Sidebar}</aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white">{Sidebar}</aside>
        </div>
      )}

      <div className="md:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur md:hidden">
          <button onClick={() => setOpen(true)} className="rounded-lg border border-slate-200 p-2" data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
          <span className="font-display font-extrabold uppercase">Back-office</span>
          <div className="w-9" />
        </header>
        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
